import { createEnterKeyHandler } from 'utils/form'
import moment from 'moment'
import * as React from 'react'
import I18n from 'core/i18n'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { Button, Select, TypeFilter } from 'components/atoms'
import SmallDateRangePickerCustomStyle from 'static/smallDateRangePickerCustomStyle'
import { DateRangePicker } from 'react-dates'
import { PostsSelector } from 'components/atoms/PostsSelector'
import Typography from 'components/atoms/_layouts/Typography'

interface IProps {
  progresses: any
  searchParams: any
  posts: any[]
}

const ORDER_BY_OPTIONS = [
  { label: 'ツアー日時 昇順', value: 'order_by_tour_day_asc' },
  { label: 'ツアー日時 降順', value: 'order_by_tour_day_desc' },
  { label: '最終取り込み日時 昇順', value: 'order_by_imported_at_asc' },
  { label: '最終取り込み日時 降順', value: 'order_by_imported_at_desc' },
]

export const IndexFilter: React.FC<IProps> = props => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false)
  const [isDetailSearchOpen, setIsDetailSearchOpen] = React.useState(
    Boolean(
      props.searchParams.seller_booking_id || props.searchParams.channel_manager_confirmation_code
    )
  )
  const [selectedProgresses, setSelectedProgresses] = React.useState<string[]>(
    props.searchParams.progresses || []
  )
  const [selectedPostIds, setSelectedPostIds] = React.useState<number[]>(
    props.searchParams.post_ids || []
  )
  const [guideName, setGuideName] = React.useState(props.searchParams.guide_name || '')
  const [sellerBookingId, setSellerBookingId] = React.useState(
    props.searchParams.seller_booking_id || ''
  )
  const [channelManagerConfirmationCode, setChannelManagerConfirmationCode] = React.useState(
    props.searchParams.channel_manager_confirmation_code || ''
  )
  const [startDate, setStartDate] = React.useState<moment.Moment>(
    props.searchParams.start_date ? moment(props.searchParams.start_date) : undefined
  )
  const [endDate, setEndDate] = React.useState<moment.Moment>(
    props.searchParams.end_date ? moment(props.searchParams.end_date) : undefined
  )
  const [focusedInput, setFocusedInput] = React.useState<any>(null)
  const [orderBy, setOrderBy] = React.useState(
    props.searchParams.order_by || 'order_by_tour_day_asc'
  )
  const isFirstRender = React.useRef(true)

  const handleCalendarOutSideRange = React.useCallback((calendarMoment: moment.Moment) => {
    return calendarMoment.isAfter(moment(), 'day')
  }, [])

  const onDateRangePickerChange = (dates: {
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }) => {
    setStartDate(dates.startDate)
    setEndDate(dates.endDate)
  }

  const changeFocusedInput = (changedFocusedInput: any) => {
    setFocusedInput(changedFocusedInput)
  }

  const handleChangePostIds = (selected: number[]) => {
    setSelectedPostIds(selected)
  }

  const applyFilter = React.useCallback(() => {
    const newURLParam = new URLSearchParams()
    const normalizedGuideName = guideName.trim()
    if (startDate) {
      newURLParam.set('start_date', startDate.format(constants.DATE_FORMAT))
    }
    if (endDate) {
      newURLParam.set('end_date', endDate.format(constants.DATE_FORMAT))
    }
    if (normalizedGuideName) {
      newURLParam.set('guide_name', normalizedGuideName)
    }

    const idFilters = {
      seller_booking_id: sellerBookingId,
      channel_manager_confirmation_code: channelManagerConfirmationCode,
    }
    Object.keys(idFilters).forEach(key => {
      const value = idFilters[key].trim()
      if (value) {
        newURLParam.set(key, value)
      }
    })

    newURLParam.set('order_by', orderBy)
    selectedProgresses.forEach(selectedProgress => {
      newURLParam.append('progresses[]', selectedProgress)
    })
    selectedPostIds.forEach(selectedPostId => {
      newURLParam.append('post_ids[]', selectedPostId.toString())
    })

    location.href = `/agent/guide_reports?${newURLParam.toString()}`
  }, [
    startDate,
    endDate,
    guideName,
    sellerBookingId,
    channelManagerConfirmationCode,
    selectedProgresses,
    selectedPostIds,
    orderBy,
  ])

  const clearFilter = React.useCallback(() => {
    location.href = `/agent/guide_reports?order_by=${orderBy}`
  }, [orderBy])

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (!focusedInput) {
      applyFilter()
    }
  }, [orderBy, focusedInput, selectedProgresses])

  const handleSearchKeyDown = createEnterKeyHandler(applyFilter)

  return (
    <Wrapper>
      <div className="filterInitialSection">
        <div className="filterSection">
          <div>
            <div className="filterCategory">
              <Typography type="subtitle">ツアー開始日</Typography>
              <SmallDateRangePickerCustomStyle />
              <DateRangePicker
                startDate={startDate}
                startDateId="start_date"
                endDate={endDate}
                endDateId="end_date"
                onDatesChange={onDateRangePickerChange}
                focusedInput={focusedInput}
                onFocusChange={(currentFocusedInput: string | null) =>
                  changeFocusedInput(currentFocusedInput)
                }
                numberOfMonths={1}
                isOutsideRange={handleCalendarOutSideRange}
                hideKeyboardShortcutsPanel={true}
                displayFormat={constants.DATE_FORMAT}
                minimumNights={0}
              />
            </div>
          </div>
          <div className="filterCategory">
            <Typography type="subtitle">ステータス</Typography>
            <TypeFilter
              className="progress_filter"
              name={I18n.t('guide_report.progress')}
              field="progresses"
              setIsFilterPanelOpen={setIsFilterPanelOpen}
              handleOnSubmit={({ progresses }) => setSelectedProgresses(progresses)}
              types={Object.keys(props.progresses).map(k => {
                return { id: k, name: props.progresses[k] }
              })}
            />
          </div>
        </div>
        <div className="filterOperations">
          <div className="buttons">
            <Button primary={true} handleClick={applyFilter}>
              検索
            </Button>
            <Button handleClick={clearFilter}>{I18n.t('generic.clear')}</Button>
          </div>
        </div>
      </div>
      <div className="detailSearchToggle" onClick={() => setIsDetailSearchOpen(open => !open)}>
        詳細検索を{isDetailSearchOpen ? '閉じる' : '開く'}
      </div>
      {isDetailSearchOpen && (
        <div className="detailSearchSection">
          <div className="filterSection">
            <div className="postIdsSelector">
              <PostsSelector
                posts={props.posts || []}
                selectedPostIds={selectedPostIds}
                onChangePostIds={handleChangePostIds}
              />
            </div>
          </div>
          <div className="textFilters">
            <div className="textFilter">
              <Typography type="subtitle">OTA予約ID</Typography>
              <input
                onKeyDown={handleSearchKeyDown}
                type="text"
                value={sellerBookingId}
                placeholder="OTA予約ID (e.g. RE-...)"
                onChange={event => setSellerBookingId(event.target.value)}
              />
            </div>
            <div className="textFilter">
              <Typography type="subtitle">Booking ref.</Typography>
              <input
                onKeyDown={handleSearchKeyDown}
                type="text"
                value={channelManagerConfirmationCode}
                placeholder="Booking ref. (VIA-.../TOK-...)"
                onChange={event => setChannelManagerConfirmationCode(event.target.value)}
              />
            </div>
            <div className="textFilter">
              <Typography type="subtitle">ガイド名</Typography>
              <input
                onKeyDown={handleSearchKeyDown}
                type="text"
                value={guideName}
                placeholder="ガイド名で検索"
                onChange={event => setGuideName(event.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="orderBy">
        <Select
          name="order_by"
          options={ORDER_BY_OPTIONS}
          defaultValue={orderBy}
          onChangeHandler={e => setOrderBy(e.target.value)}
        />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;

  .orderBy {
    margin-top: 12px;
    margin-left: auto;
  }

  .filterSection {
    display: flex;
    gap: 20px;
    align-items: end;
    > button {
      margin: 0;
    }
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      flex-direction: column;
      align-items: flex-start;
    }

    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      width: 100%;
    }
  }

  .filterInitialSection {
    display: flex;
    align-items: end;
    justify-content: space-between;
  }

  .filterCategory {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: space-between;
  }

  .filterOperations {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .Filter_Label {
    font-size: 12px;
  }

  .postIdsSelector {
    flex: 1;
    min-width: 400px;
    font-size: 14px;
  }

  .textFilter input {
    display: block;
    width: 100%;
    height: 40px;
    padding: 8px 12px;
    border: solid 1px ${constants.COLORS.divider};
    border-radius: 4px;
    font-size: 14px;
    transition: border 0.2s ease;
    outline: none;
    background: none;
  }

  .textFilter input:focus {
    border: solid 1px ${constants.COLORS.primary};
  }

  .buttons {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .detailSearchSection {
    flex-basis: 100%;
  }

  .detailSearchToggle {
    cursor: pointer;
    margin-left: auto;
    font-size: 12px;
    color: ${constants.COLORS.subText};
    text-decoration: underline 1px solid ${constants.COLORS.subText};
  }

  .textFilters {
    display: flex;
    gap: 12px;
    margin-top: 12px;
  }

  .textFilter {
    display: inline-flex;
    flex-direction: column;
    gap: 4px;
    width: 220px;
  }
`
